export const accountEnTranslation = {
  account: {
    plaidIdentityVerification: {
      title: "Identity Verification Required",
      description:
        "To continue with your lease application, we need to verify your identity. This process is secure and handled by our trusted partner Plaid.",
      startButton: "Start Identity Verification",
      startingButton: "Starting verification...",
      completeButton: "Verification Complete",
      successMessage: "Your identity has been successfully verified",
      failureMessage: "Identity verification failed. Please try again.",
      failure: {
        DOC_IMAGE_QUALITY:
          "Your ID photo wasn't clear enough. Please retake it in good lighting, hold the camera steady, and avoid glare.",
        DOC_COMPOSITION:
          "Your ID photo couldn't be framed correctly. Place the ID flat on a dark surface and capture the entire document.",
        DOC_AUTHENTICITY:
          "We couldn't confirm the authenticity of the document. Try a different government-issued ID.",
        DOC_UNSUPPORTED:
          "The document you uploaded isn't supported. Please use a passport, driver's license, state ID, permanent resident card, or temporary visa.",
        DOC_GENERIC:
          "We couldn't verify your ID document. Please try again with a clearer photo.",
        SELFIE_LIVENESS:
          "Your selfie didn't pass our liveness check. Please retake it looking straight at the camera in a well-lit space.",
        SELFIE_NO_MATCH:
          "Your selfie didn't match the photo on your ID. Please retake it with your face clearly visible.",
        SELFIE_GENERIC: "We couldn't verify your selfie. Please try again.",
        KYC_DOB_MISMATCH:
          "The date of birth you entered doesn't match your ID. Please double-check it and try again.",
        KYC_NAME_MISMATCH:
          "The name you entered doesn't match your ID. Please use your name exactly as it appears on your government-issued ID.",
        KYC_ADDRESS_MISMATCH:
          "The address you provided doesn't match our records. Please check and re-enter it.",
        KYC_ID_MISMATCH:
          "The ID number couldn't be matched. Please re-check your entry.",
        KYC_PHONE_MISMATCH:
          "The phone number you entered doesn't match our records.",
        KYC_GENERIC:
          "Some of the information you entered didn't match your ID. Please review your name, date of birth, and address.",
        RISK_DECLINED:
          "We weren't able to complete your verification. Please contact support.",
        WATCHLIST_FLAGGED:
          "We weren't able to complete your verification. Please contact support.",
        INCOMPLETE: "Your verification wasn't finished. Please try again.",
        UNKNOWN: "Identity verification failed. Please try again.",
      },
      contactSupport: "Need help? Contact us at",
      errorMessage: "Failed to initialize verification. Please try again.",
      acceptedDocumentsTitle: "Documents you can use",
      acceptedDocuments: {
        passport: "Passport",
        driversLicense: "Driver's license",
        stateId: "State ID",
        residentCard: "Permanent resident card",
        temporaryVisa: "Temporary visa",
      },
    },
    registerProspect: {
      provideInfoTitle:
        "Provide additional information to complete registration",
      createAccountTitle: "Create an account to register",
      alreadyRegistered: "Already Registered?",
      clickToLogin: "Click here to Login",
      firstNameLabel: "Your First Name (as shown on your government-issued ID)",
      middleNameLabel: "Your Middle Name (if applicable)",
      lastNameLabel: "Your Last Name (as shown on your government-issued ID)",
      phoneLabel: "Phone",
      emailLabel: "Email",
      passwordLabel: "Password",
      confirmPasswordLabel: "Confirm Password",
      consentCommsLabel:
        "I consent to receive relevant email and/or text message at the email and/or telephone number above. Standard data & messaging rates may apply.",
      privacyPolicyLabel: "I have read and agree to the ",
      privacyPolicyLink: "Privacy Policy",
      rentalCriteriaLabel: "I have read and agree to the ",
      rentalCriteriaLink: "Rental Criteria",
      createAccountButton: "Create Account",
      continueButton: "Continue",
      applicationNoticeTitle: "Application Notice",
      applicationNoticeDescription1:
        "Before you continue, please have one government-issued photo ID ready. This may include: <ul><li>Passport (US and non-US issued passports are accepted)</li><li>US Driver's License</li><li>US State ID</li><li>Permanent Resident Card</li><li>Temporary Visa</li></ul>",
      applicationNoticeDescription2:
        "<strong>Application Fee:</strong> Each applicant must pay a non-refundable application fee to cover third-party and administrative processing costs. Payment of the fee does not guarantee approval.",
      applicationNoticeDescription3:
        "<strong>Administrative Fee:</strong> In addition to the application fee, the household must pay an administrative fee. This fee does not guarantee approval, and the apartment will not be leased until your application is complete and all required documents/monies are received. Required documents must be provided within 72 hours to avoid delays or possible denial.",
      applicationNoticeDescription4:
        "<strong>Income Requirements:</strong> All leaseholders’ combined gross monthly income must meet the property’s minimum requirement and will be verified prior to approval. Acceptable verification may include pay stubs, bank statements, benefits/support documentation, offer letters, or qualifying assets. Self-employed and tip/commission-based applicants may need additional documentation, and students who do not meet the requirement may need a qualified cosigner.",
      registrationErrorHeading: "Registration Error",
      errorMessage: "There is an error. Please Try again.",
      inviteAuthMismatchTitle: "Wrong account for this invite",
      inviteAuthMismatchBody:
        "This invitation is for <strong>{{email}}</strong>, but you're signed in as a different account. Sign out, then open the invite link again to continue as that applicant.",
      inviteAuthSignOutButton: "Sign out and continue",
      // Validation errors
      required: "Please complete this field",
      emailInvalid: "Please enter a valid email address",
      phoneInvalid: "Phone number required 10 digits",
      nameMustIncludeBoth: "Name must include both first and last name",
      passwordMinLength: "Password must be at least 8 characters long",
      passwordUppercase: "Password must contain at least one uppercase letter",
      passwordLowercase: "Password must contain at least one lowercase letter",
      passwordNumber: "Password must contain at least one number",
      passwordsMustMatch: "Passwords must match",
      noLeasingAgent: "None",
      languageLabel: "Preferred Language",
      prospectSourceLabel: "How did you hear about us?",
      referrerNameLabel:
        "Name and Unit Number of Referrer (if any) (eg John Doe, Apt 4B)",
      leasingAgentLabel: "Are you working with a leasing agent?",
    },
    unitDetails: {
      title: "Unit Details",
      unitNumber: "Unit Number",
      beds: "Bed(s)",
      baths: "Bath(s)",
      term: "Term",
      months: "Months",
      moveIn: "MoveIn",
      rent: "Base Rent",
      deposit: "Security Deposit",
      recurringCharges: "Recurring Monthly Charges",
      oneTimeCharges: "One-Time Charges",
      perMonth: "per month",
      latestMoveInPossibleFrom: "Latest MoveIn Possible from",
      rentUpdatedTo: "Rent updated to",
      from: "from",
      basedOnMoveIn: "based on MoveIn",
      feesDisclaimer:
        "Other fees billed by our utility provider may apply, the fees listed above are billed through our property management system",
      studio: "Studio",
    },
    leaseApplication: {
      stepTitle: "Step",
      step: "Step",
      of: "of",
      next: "Next",
      previous: "Previous",
      submit: "Submit",
      // Common fields
      name: "Name",
      email: "Email",
      phone: "Phone",
      address: "Address",
      country: "Country",
      street: "Street Address",
      city: "City",
      state: "State",
      zip: "Zip",
      // Step titles
      idVerification: "ID Verification",
      applicantInfo: "Applicant Info",
      currentAddress: "Address",
      employment: "Income",
      petsVehicles: "Pets, Vehicles & Rentables",
      termsConditions: "T & C",
      review: "Review",
      members: "Members",
      // Applicant Info
      birthdate: "Birthdate",
      identification: "Identification",
      identificationNumber: "Identification Number",
      identificationType: "Type",
      ssn: "Social Security Number",
      ssnOptional: "Social Security Number *",
      noSsn: "I don't have SSN",
      ssnFormat: "Format: XXX-XX-XXXX",
      usCitizen: "I am a US Citizen",
      emergencyContact: "Emergency Contact",
      emergencyContactName: "Name",
      relationship: "Relationship",
      uploadFrontPicture: "Upload Front Picture",
      uploadBackPicture: "Upload Back Picture",
      idVerificationProcess: "Identity Verification Process",
      // ID Types
      driversLicense: "Drivers License",
      passport: "Passport",
      idCard: "ID Card",
      residencyPermit: "Residency Permit",
      temporaryVisa: "Temporary Visa",
      // Address
      currentAddressTitle: "Current Address",
      moveInDate: "Move in date at current residence",
      reasonForLeaving: "Reason for Leaving",
      areYouRenting: "Is this a rental address?",
      landlordName: "Community or Landlord Name",
      contactName: "Contact Name",
      landlordPhone: "Phone",
      // Employment
      employmentTitle: "Income",
      employmentDescription:
        "📝 We use Proofup as our income verification partner. You will receive an option to verify your income through our secure verification process at the last step of this process. To be eligible for renting at this property, you need to have income 2.5x the rent amount.",
      employmentDescriptionWithGuarantor:
        "<br/>⚠️ Income requirements not meeting. If you and your co-applicants doesn't meet income criteria together and you think you can meet with a guarantor, you can add one below.",
      notApplicable: "Not Applicable",
      industry: "Industry",
      employer: "Employer",
      title: "Title",
      startDate: "Start",
      endDate: "End",
      monthlyIncome: "Monthly Income",
      supervisorName: "Supervisor Name",
      supervisorPhone: "Phone",
      currentlyEmployed: "I currently work here",
      emailAddress: "Email Address",
      otherIncomeSources: "Additional Income Sources",
      otherIncomeHelp:
        "Optional. Add other income (child support, benefits, gifts, etc.) that should count toward qualification.",
      addIncome: "Add Income",
      incomeType: "Type",
      effectiveDate: "Effective Date",
      incomeFrequency: "Income Frequency",
      incomeAmount: "Income Amount",
      noIncome: "I don't have any income",
      noIncomeDescription:
        "You indicated no income. This property requires an income of at least {{incomeLevel}}x the monthly rent to qualify.",
      noIncomeDescription1:
        "Your income does not meet the requirement of {{incomeLevel}}x the monthly rent. This property requires an income of at least {{incomeLevel}}x the monthly rent to qualify.",
      noIncomeDescription2:
        "This property requires an income of at least {{incomeLevel}}x the monthly rent to qualify.",
      useCoApplicantIncomeTitle: "Use Co-Applicant Income?",
      useCoApplicantIncomeDescription:
        "We noticed you have household members added to your application. Would you like to continue with a co-applicant's income to strengthen your application?",
      // Pets & Vehicles
      petsVehiclesTitle: "Anything else you'd like to add?",
      pets: "Pets",
      addPets: "Add Pets",
      addPetsMax: "Add Pets (Max {{count}} Per Unit)",
      maxPetsPerUnit: "Max 2 Per Unit",
      petPolicyUnavailable:
        "Contact the leasing office for pet policy details for this unit.",
      petName: "Name",
      petType: "Type",
      breed: "Breed",
      color: "Color",
      age: "Age (years)",
      weight: "Weight (lbs.)",
      vehicles: "Vehicles",
      addVehicle: "Add Vehicle",
      hasVehiclesQuestion: "Do you have any vehicles to register?",
      year: "Year",
      make: "Make",
      model: "Model",
      licensePlate: "License plate number",
      licensePlateState: "State",
      // Members
      willAnyoneElseLive: "Will anyone else be living here?",
      willAnyoneElseLiveHelp:
        "Include every adult and minor who will live in the unit (not including yourself).",
      notAnswered: "Not answered",
      addMembers: "Add Members",
      householdStatus: "Relationship",
      spouse: "Spouse/Cohabitant",
      familyAdult: "Family Member - Adult",
      familyMinor: "Family Member - Minor",
      roommate: "Roommate",
      birthDate: "Birth Date",
      addGuarantors:
        "Do you want to add any guarantors to support your application?",
      addGuarantor: "Add Guarantor",
      addGuarantorsQuestion:
        "Do you want to add any guarantors to support your application?",
      addGuarantorsDescription:
        "Do you want to add any guarantors to support your application?",
      guarantor: "Guarantor",
      anythingElseToAdd: "Anything else you'd like to add?",
      // Terms & Review
      termsConditionsTitle: "Terms and Conditions",
      agreeToTerms: "I agree to the terms and conditions",
      reviewTitle: "Review Information",
      confirmInformation:
        "I confirm the information provided is correct to my knowledge",
      // Validation messages
      phoneValidation: "Phone should be 10 digits",
      agreeValidation: "You must agree to the terms and conditions",
      dependentAgeValidation: "Dependents should be less than 18 years old",
      fullNameValidation: "Please enter both first and last name",
      // Pet types
      dog: "Dog",
      cat: "Cat",
      bird: "Bird",
      fish: "Fish",
      other: "Other",
      // Array field template
      addPet: "Add Pet",
      addMember: "Add Member",
      addRentableItem: "Add Rentable Item",
      addIncomeSource: "Add Income Source",
      addItem: "Add Item",
      pet: "Pet",
      vehicle: "Vehicle",
      member: "Member",
      rentableItem: "Rentable Item",
      rentableItems: "Rentable Items",
      incomeSource: "Income Source",
      item: "Item",
      noPetsAdded: "No pets added yet",
      noVehiclesAdded: "No vehicles added yet",
      noMembersAdded: "No household members added yet",
      noGuarantorsAdded: "No guarantors added yet",
      noIncomeSourcesAdded: "No additional income sources added yet",
      noItemsAdded: "No items added yet",
      noRentableItemsAdded: "No rentable items added yet",
      remove: "Remove",
      // Review labels
      number: "Number",
      type: "Type",
      yes: "Yes",
      no: "No",
      driversLicenseTitle: "Driver's License",
      landlord: "Landlord",
      landlordContact: "Landlord Contact",
      currentEmploymentTitle: "Current Employment",
      otherIncomeTitle: "Other Income",
      emergencyContactTitle: "Emergency Contact",
      vehicleInformationTitle: "Vehicle Information",
      householdMembersTitle: "Household Members",
      miscellaneousTitle: "Miscellaneous",
      feeAndDepositTitle: "Fee And Deposit",
      supervisor: "Supervisor",
      supervisorEmail: "Supervisor Email",
      noPets: "No Pets",
      noVehicles: "No Vehicles",
      registration: "Registration",
      assistant: "Assistant",
      howDidYouHear: "How did you hear about us",
      referringAgent: "Referring Agent",
      referringAgency: "Referring agency",
      otherComments: "Other Comments",
      reasonForMoving: "Reason for Moving",
      feeReviewText:
        "Please review the fees below.You will be redirected to a page to complete your payment",
      applicationFee: "Application Fee",
      unitFee: "Administrative Fee",
      // Pet policy
      allowed: "Allowed",
      notAllowed: "Not Allowed",
      fees: "Fees",
      rent: "Rent",
      // Validation errors
      required: "is a required property",
      minLength: "should NOT be shorter than",
      maxLength: "should NOT be longer than",
      minimum: "should be >=",
      maximum: "should be <=",
      pattern: "does not match the required pattern",
      petPattern: "Can't have special characters",
      licensePlatePattern:
        "Enter a valid license plate — letters and numbers only, up to 10 characters",
      format: "should match format",
      enum: "should be equal to one of the allowed values",
      invalidEmail: "should be a valid email address",
      invalidDate: "should be a valid date",
      invalidNumber: "should be a valid number",
      noAddressProvided: "No address provided",
    },
    incomeVerification: {
      title: "Income Verification",
      requiredError: "Income verification is required to proceed.",
      subtitle: "📊 Verify Your Income with Proofup",
      alreadyCompleted:
        "Income verification has already been completed. You can proceed to the next step.",
      instruction:
        "Please verify your income using the secure verification process below.",
      partner:
        "We partner with Proofup to provide a secure and efficient income verification process.",
      description:
        "To complete your lease application, we offer income verification through our trusted partner Proofup. This secure process helps streamline your application and may expedite approval.",
      videoTitle: "How income verification works",
      whatYouNeedTitle: "What you'll need:",
      needItem1: "Access to your online banking or payroll account",
      needItem2: "Recent pay stubs or bank statements (if needed)",
      needItem3: "About 5-10 minutes to complete the process",
      startButton: "Start Verification",
      openingButton: "Opening…",
      waitingButton: "Waiting for completion…",
      verifiedButton: "Verification Completed",
      retryButton: "Retry Verification",
      skipButton: "Skip for now - Continue to Payment",
      skipForNow: "Skip for now",
      verifyIncomeCta: "Verify Income",
      postPaymentIntro:
        "Verify your income to help speed up your application review. You can do it now, or come back to it anytime from your applications.",
      securityNote:
        "Your information is secure and encrypted. Proofup follows industry-standard security practices to protect your financial data.",
      invalidRequestTitle: "Invalid Request",
      invalidRequestMessage:
        "No lease ID provided. Please return to the application.",
      goHomeButton: "Go Home",
    },
    leasePayment: {
      applicantInformation: "Applicant Information",
      applicantName: "Applicant Name",
      paymentSuccess: "Payment Success",
      paymentSuccessMessage:
        "Your Payment was Successful! We will contact you shortly with any updates on your application status",
      balanceDue: "Balance Due",
      total: "Total",
      payment: "Payment",
      selectAmount: "Select Amount",
      optionBalance: "Application and Administrative Fee",
      optionTotalLeaseBalance: "Total Lease Balance",
      optionYourFee: "Your application and admin fee",
      optionHouseholdFees: "Fees for all adults",
      optionCustomAmount: "Custom Amount",
      customAmountPlaceholder: "Enter custom amount",
      customAmountWarning:
        "Applications will be processed after all required fees are paid and any outstanding balances are paid in full. (Use this option only if directed by a Leasing Consultant.)",
      payAmount: "Amount to Pay",
      amountLoading: "Loading",
      cardTab: "Card",
      achTab: "ACH",
      noFeeDueHelp:
        "Your application fee is $0.00 (waived or not required). Click below to submit without payment.",
      completeApplication: "Complete Application",
      debitCard: "Debit Card",
      creditCard: "Credit Card",
      cardHolderName: "Card Holder Name",
      cardPaymentError: "Card Payment",
      unitUnavailableHeading: "Unit no longer available",
      unitUnavailableBody:
        "The unit you were trying to book is no longer available. Please choose a different unit.",
      viewAvailableUnits: "View available units",
      unit: "Unit",
      monthlyRent: "Monthly rent",
      moveInTooSoon: {
        heading: "Move-in date needs to be later",
        body: "Please choose a later move-in date to continue.",
        pickDate: "Choose a new move-in date",
        saveAndContinue: "Save date and continue",
        saveError: "We couldn't update your move-in date. Please try again.",
        confirmRentHeading: "Confirm updated rent",
        confirmRentBody:
          "This move-in date changes your monthly rent. Confirm the new amount to continue.",
        leaseTerm: "Lease term",
        months: "{{term}} months",
        previousRent: "Previous rent",
        newRent: "New monthly rent",
        confirmRent: "Confirm rent and continue",
        cancel: "Cancel",
      },
      submitButton: "Submit",
      validation: {
        required: "Please complete this field",
        emailInvalid: "Please enter a valid email address",
        phoneNumberInvalid: "Phone number required 10 digits",
        accountNumberInvalid: "Input required between 5 and 17 digits",
        accountsNotMatch: "Accounts do not match",
        routingNumberInvalid: "Input required only 9 digits",
        selectAccountType: "Please select the account type",
        zipcodeInvalid: "Input required only 5 digits",
        selectCardType: "Please select the card type",
        namesRequired: "Please enter the last name.",
        emptyCardNumber: "Please enter a valid card number",
        invalidCardNumber: "The card number is invalid",
        emptyExpiryDate: "Please enter a valid expiration date",
        monthOutOfRange: "The expiration month must be between 01 and 12",
        yearOutOfRange: "The expiration year cannot be in the past",
        dateOutOfRange: "Expiration date cannot be in the past",
        invalidExpiryDate: "The expiration date is invalid",
        emptyCVC: "Please enter a valid security code",
        invalidCVC: "The security code is invalid",
      },
    },
  },
};
